import '@ant-design/compatible/assets/index.css';
import { useQuery } from 'react-query';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Col, Spin, Popconfirm, Space, DatePicker } from 'antd';
import DF from 'DisplayFramework';
import { toCurrency } from 'utils/utils';

import Form, { FormInstance } from 'antd/lib/form';

import { PlusOutlined } from '@ant-design/icons';

import InputCurrency from 'components/InputCurrency';
import {
  ClientInvoiceScheduleResponse,
  getClientInvoiceById,
} from 'services/clientInvoice';
import { useFormList } from 'hooks/useFormList';
import _, { isNil, sumBy } from 'lodash';
import moment from 'moment';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import ThirdPartyPayerList from 'pages/ClientInvoice/CIDetail/components/ThirdPartyPayer';
import {
  dateToString,
  indexBy,
  shouldUpdateCompare,
  toDatePickerDate,
} from 'utils/utils2';
import CIPayerSelect from 'pages/ClientInvoice/CIDetail/components/CIPayerSelect';
import Value from 'components/Value';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import { useForm } from 'antd/lib/form/Form';
import ParBankAccountSelect from 'components/ParBankAccountSelect';
import GetLabel from 'DisplayFramework/components/GetLabel';

export type TabCIPayScheduleProps = {
  objectid: number;
  form: FormInstance;
  setSuccessful: any;
};

const TabCIPaySchedule = ({
  objectid,
  form: formW,
  setSuccessful,
}: TabCIPayScheduleProps) => {
  const fieldName = 'clientInvoiceScheduleViewList';
  const [form] = useForm();

  const { data: clientInvoice, isLoading } = useQuery(
    ['getClientInvoiceById', { ciid: objectid }],
    () =>
      getClientInvoiceById({ ciid: objectid }).then((res) => res.data ?? {}),
    {
      enabled: !isNil(objectid),
    }
  );

  useEffect(() => {
    setSuccessful(isLoading);
  }, [isLoading]);

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'cisid',
  });
  const getName = useCallback(
    (name: string) => (record: ClientInvoiceScheduleResponse) => {
      const schedules = form.getFieldValue(fieldName);
      const index = indexBy(schedules, 'cisid')[record.cisid];
      return [fieldName, index, name];
    },
    [form]
  );
  const addRow = () => {
    const totalInvoiceAmount: number = form.getFieldValue('cigrosstotal');
    const totalPaymentsScheduled: number = form.getFieldValue('cisamount');
    let leftAmount: number = totalInvoiceAmount;

    if (sumBy(fieldName, 'cisamount') && totalPaymentsScheduled) {
      leftAmount = totalPaymentsScheduled - totalInvoiceAmount;
    }
    const cisamount = totalPaymentsScheduled
      ? leftAmount - totalPaymentsScheduled
      : leftAmount;
    const newcisorder = form.getFieldValue(fieldName).length + 1;

    const item: any = {
      cisid: Math.round(_.random(10000) * 10000000),
      new: true,
      cisamount,
      cisdueamount: cisamount,
      cisduedate: moment() as any,
      cisorder: newcisorder,
      cispaymentdelaycode: '',
      cispaymentmode: '',
      cisstatus: 'INIT',
      cistype: 'PAYMENT',
      mgbankaccount: '' as any,
      tpidpayer: undefined,
      tppayerbankaccount: '',
    };

    add(item);
    recalculate(0, { cisamount: cisamount });
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(
        fieldName
      ) as ClientInvoiceScheduleResponse[];

      let newValues = values.map((item, i) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const cisamount = updatedItem.cisamount;
        const cisdueamount = updatedItem.cisamount;

        return {
          ...updatedItem,
          cisamount,
          cisdueamount,
        };
      });

      let cisamount = sumBy(newValues, 'cisamount');
      if (cisamount > form.getFieldValue('cigrosstotal')) {
        let amounttoreduce = cisamount - form.getFieldValue('cigrosstotal');
        newValues = newValues.map((item, i) => {
          if (i === index) return item;

          if (item.cisamount > amounttoreduce && amounttoreduce > 0) {
            const amount = amounttoreduce;
            amounttoreduce = 0;
            return {
              ...item,
              cisamount: item.cisamount - amount,
              cisdueamount: item.cisamount - amount,
            };
          } else return { ...item };
        });
        cisamount = sumBy(newValues, 'cisamount');
      }

      form.setFieldsValue({
        [fieldName]: newValues,
        cisamount,
      });
      formW.setFieldsValue({
        [fieldName]: newValues,
        cisamount,
      });
    },
    [form, formW]
  );
  useEffect(() => {
    if (clientInvoice) {
      form.setFieldsValue({
        [fieldName]: clientInvoice.clientInvoiceSchedules.map((line) => ({
          ...line,
          cisduedate: toDatePickerDate(line.cisduedate),
        })),
        thirdparty: {
          tpid: clientInvoice.tpidclient,
          tpname: clientInvoice.clientname,
          tpreference: clientInvoice.clientreference,
        },
        cigrosstotal: clientInvoice.cigrosstotal,
        cisamount: sumBy(clientInvoice.clientInvoiceSchedules, 'cisamount'),
        currcode: clientInvoice.currcode,
      });
      formW.setFieldsValue({
        cigrosstotal: clientInvoice.cigrosstotal,
        cisamount: sumBy(clientInvoice.clientInvoiceSchedules, 'cisamount'),
        [fieldName]: clientInvoice.clientInvoiceSchedules.map((line) => ({
          ...line,
          cisduedate: toDatePickerDate(line.cisduedate),
        })),
      });
    } else
      form.setFieldsValue({
        [fieldName]: [],
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectid, clientInvoice, form]);
  // const onChangePayer = (e: any) => {
  //   const selectedid = Number(e);
  //   const list = state.payerList;
  //   const res = list.filter((rowItem: any) => rowItem.tpid === selectedid)[0];
  //   return res;
  // };

  const insertpayer = (obj: any, index: any) => {
    const values = form.getFieldValue(
      fieldName
    ) as ClientInvoiceScheduleResponse[];
    const newValues = values.map((item, i) => {
      if (i !== index) {
        return item;
      }

      let newDate = moment().add(
        obj?.tppaydelayin
          ?.replace('M', '')
          .substr(1, obj?.tppaydelayin?.length),
        'days'
      );
      if (obj?.tppaydelayin?.indexOf('M') > -1) {
        newDate = moment(newDate).endOf('month');
      }

      const tpbkid = obj?.tpbklist.find(
        (bk: any) =>
          bk.bkstatus === 'VALID' && ['INOUT', 'OUT'].includes(bk.bktype)
      )?.tpbkid;
      const updatedItem = {
        ...item,
        tpbkid: tpbkid,
        cispaymentdelaycode: obj?.tppaydelayin ? obj?.tppaydelayin : '',
        cispaymentmode: obj?.tppaymodein ? obj?.tppaymodein : '',
        tpbeneficiarybankaccount: tpbkid,
        tpidpayer: obj?.tpid ? obj?.tpid : undefined,
        tppayerbankaccount: tpbkid,
        cisduedate: newDate,
        tpbklist: obj?.tpbklist,
      };

      return {
        ...updatedItem,
      };
    });
    form.setFieldsValue({
      [fieldName]: newValues,
    });

    formW.setFieldsValue({
      [fieldName]: newValues,
    });
  };

  const columns = useMemo<DataGridColDef<ClientInvoiceScheduleResponse>[]>(
    () => [
      {
        comcode: 'CILinesSchedule.cistype',

        key: 'ciltype',
        width: 120,
        fixed: 'left',
        render: (_, record, index) => (
          <Form.Item name={getName('cistype')(record)}>
            <ParListSelect
              parcode="CISTYPE"
              disabled={
                record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.tpidpayer',
        key: 'tpidpayer',
        width: 150,
        render: (_, record, index) => (
          // <Form.Item name="clientname">
          //   <Select
          //     allowClear
          //     onFocus={() => fetchtpidpayer()}
          //     onChange={(e) => {
          //       insertpayer(onChangePayer(e), index);
          //     }}
          //     defaultValue={state?.payerList[0]?.tpname}
          //     disabled={ciid !== undefined}
          //   >
          //     {state.payerList?.map((d: any) => (
          //       <Option key={d.tpid} value={d.tpid}>
          //         {d.tpname}
          //       </Option>
          //     ))}
          //   </Select>
          // </Form.Item>
          <Form.Item
            shouldUpdate={form.getFieldValue('thirdparty')}
            name={getName('tpidpayer')(record)}
            key={index}
          >
            <ThirdPartyPayerList
              selectedV={form.getFieldValue(getName('tpidpayer')(record))}
              disabled={
                record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
              }
              key={index}
              onSelected={(e) => insertpayer(e, index)}
              tpid={form.getFieldValue(['thirdparty', 'tpid'])}
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.cisamount',
        dataIndex: 'cisamount',
        width: 150,
        render: (_value, _record, index) => (
          <Form.Item name={getName('cisamount')(_record)}>
            <InputCurrency
              currcode={form.getFieldValue('currcode')}
              onChange={(value) => recalculate(index, { cisamount: value })}
              disabled={
                _record.cisstatus !== 'VALID' && _record.cisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.cispaymentdelaycode',
        key: 'cispaymentdelaycode',
        width: 120,
        render: (_, record, index) => (
          <Form.Item
            name={getName('cispaymentdelaycode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYDELAYIN"
              onSelected={(value) => {
                let newDate = moment().add(
                  value.replace('M', '').substr(1, value.length),
                  'days'
                );
                if (value.indexOf('M') > -1) {
                  newDate = moment(newDate).endOf('month');
                }
                recalculate(index, {
                  cisduedate: newDate,
                });
              }}
              disabled={
                record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.cisduedate',
        key: 'cisduedate',
        dataIndex: 'cisduedate',
        width: 120,
        render: (value, record, index) => {
          return (
            <Form.Item name={getName('cisduedate')(record)}>
              {record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT' ? (
                dateToString(value ?? '')
              ) : (
                <DatePicker />
              )}
            </Form.Item>
          );
        },
      },
      {
        comcode: 'CILinesSchedule.cispaymentmode',
        key: 'cispaymentmode',
        width: 150,
        render: (_, record, index) => (
          <Form.Item
            name={getName('cispaymentmode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYMODEIN"
              disabled={
                record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.tppayerbankaccount',
        key: 'tppayerbankaccount',
        width: 180,
        render: (_, record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item name={getName('tppayerbankaccount')(record)}>
                <CIPayerSelect
                  tpid={getFieldValue(['thirdparty', 'tpid'])}
                  // cispaymentmode={getFieldValue([
                  //   fieldName,
                  //   index,
                  //   'cispaymentmode',
                  // ])}
                  selectedPayer={getFieldValue(getName('tpidpayer')(record))}
                  disabled={
                    record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'mgbankaccount',
        key: 'mgbankaccount',
        width: 150,
        render: (_, record, index) => (
          <Form.Item shouldUpdate={shouldUpdateCompare('currcode')} noStyle>
            {({ getFieldValue }) => (
              <Form.Item
                name={getName('mgbankaccount')(record)}
                rules={[
                  {
                    required:
                      record.cispaymentmode === 'TRANSFER' ||
                      record.cispaymentmode === 'DDEBIT',
                  },
                ]}
              >
                <ParBankAccountSelect
                  mgid={Number(localStorage.getItem('mgid'))}
                  direction="IN"
                  currcode={getFieldValue('currcode')}
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.cisstatus',
        key: 'cisstatuslabel',
        width: 120,
        render: (_value, _record, index) => (
          <Form.Item name={getName('cisstatuslabel')(_record)}>
            <Value />
          </Form.Item>
        ),
      },
      {
        comcode: 'CILinesSchedule.cisdueamount',
        key: 'cisdueamount',
        render: (_value, _record, index) => (
          <Form.Item name={getName('cisdueamount')(_record)}>
            <Value
              formatter={(value) =>
                toCurrency(value, form.getFieldValue('currcode'))
              }
            />
          </Form.Item>
        ),
        width: 120,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record, index) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              form.setFieldsValue({
                cisamount: form.getFieldValue('cisamount') - record.cisamount,
              });
              remove(record.cisid);
            }}
          >
            <DeleteButton
              onlyIcon
              disabled={
                record.cisstatus !== 'VALID' && record.cisstatus !== 'INIT'
              }
            />
          </Popconfirm>
        ),
        align: 'center',
        width: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, remove]
  );

  const title = (
    <>
      <Col span={16} />
      <Form.Item
        noStyle
        shouldUpdate={shouldUpdateCompare(['cisamount', 'cigrosstotal'])}
      >
        {({ getFieldValue }) => {
          return (
            <Space size={16}>
              <span style={{ color: '#668cff' }}>
                <GetLabel label="Total Invoice Amount :" />{' '}
                {toCurrency(
                  getFieldValue('cigrosstotal'),
                  getFieldValue('currcode')
                )}
              </span>
              <strong
                style={{
                  color:
                    getFieldValue('cisamount') === getFieldValue('cigrosstotal')
                      ? 'green'
                      : getFieldValue('cisamount') >
                        getFieldValue('cigrosstotal')
                      ? 'red'
                      : 'orange',
                }}
              >
                <GetLabel label="Total Payments Scheduled:" />{' '}
                {toCurrency(
                  getFieldValue('cisamount'),
                  getFieldValue('currcode')
                )}
              </strong>
            </Space>
          );
        }}
      </Form.Item>
    </>
  );

  const getTotalCisamount = () => {
    return _.sumBy(form.getFieldValue(fieldName), 'cisamount');
  };

  return (
    <DF.Provider pagcode="tabsipay">
      <Form.Item
        name="clientInvoiceScheduleViewList"
        rules={[
          {
            required: true,
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('cigrosstotal') !== getTotalCisamount()) {
                return Promise.reject(
                  new Error(
                    'Total Invoice Amount should be equal to Total Payments Scheduled'
                  )
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      />
      <Form
        form={form}
        onValuesChange={() => {
          formW?.setFieldsValue({
            [fieldName]: form.getFieldValue([fieldName]),
          });
        }}
      >
        <Spin spinning={isLoading}>
          <div style={{ padding: 10, background: 'white' }}>
            <Form.Item
              noStyle
              shouldUpdate={shouldUpdateCompare(
                'clientInvoiceScheduleViewList'
              )}
            >
              {({ getFieldValue }) => (
                <DataGrid
                  title={title}
                  columns={columns}
                  dataSource={getFieldValue(fieldName)}
                  rowKey="cisid"
                  scroll={{ x: 1200, y: 360 }}
                  hideFilters
                  extraActions={
                    <ButtonOperation
                      icon={<PlusOutlined />}
                      type="primary"
                      onClick={addRow}
                      comcode="add"
                      disabled={
                        getFieldValue(['thirdparty', 'tpid']) === undefined
                      }
                    />
                  }
                />
              )}
            </Form.Item>
          </div>
        </Spin>
      </Form>
    </DF.Provider>
  );
};
export default TabCIPaySchedule;
